<template>
  <div ref="text" v-html="lyricsText"></div>

  <div id="lyricsText" ref="offcanvas" class="offcanvas offcanvas-end offcanvas-annotation ms-0 border-0" tabindex="-1" aria-labelledby="lyricsText">
    <div class="offcanvas-header px-20px">
      <h3 class="offcanvas-title my-0" id="offcanvasLabel">Vysvetlivka textu</h3>
      <button type="button" class="btn-close text-reset ms-auto" data-bs-dismiss="offcanvas" aria-label="Close" @click="resetAnnotation"></button>
    </div>

    <div class="offcanvas-body px-20px pt-0 mt-0">
      <div v-if="!loading">
        <div v-if="!selectedAnnotation">
          <div v-if="user" v-show="selectedText" class="mt-10px">
            <strong>Vybral/a si text:</strong>

            <div class="mb-10px" v-html="selectedTextHtml"></div>

            <button class="btn btn-primary" @click="createAnnotationHandler">Vytvoriť vysvetlivku</button>
          </div>

          <div v-if="!user">
            Na vytvorenie vysvetlivky musíš byť <a :href="loginUrl">prihlásený</a>.
          </div>
        </div>

        <div v-else>
          <div v-html="selectedAnnotation.html" class="mb-20px"></div>

          <div v-if="selectedAnnotation.text">
            <UserCreationPanel :user="selectedAnnotation.user" :created="selectedAnnotation.created" />

            <div v-show="!editMode">
              <div class="annotation-text mt-15px" v-html="selectedAnnotation.text"></div>
            </div>

            <div v-show="selectedAnnotation.canEdit || selectedAnnotation.canDelete">
              <div v-if="!editMode" class="d-flex align-items-center justify-content-between gap-5px mt-5px">
                <a v-if="selectedAnnotation.canEdit" class="btn btn-link p-0" role="button" @click="this.editMode = !this.editMode;">Upraviť</a>
                <a v-if="selectedAnnotation.canDelete" class="btn btn-link p-0" role="button" @click="deleteAnnotationHandler">Odstrániť</a>
              </div>
            </div>

            <div v-if="!editMode" class="d-flex align-items-center justify-content-between mt-10px">
              <VoteWrapper :vote-property="'fk_annotation'" :vote-value="selectedAnnotation.id" :can-vote="selectedAnnotation.canVote" />

              <Report v-if="selectedAnnotation.canReport" :entity-name="'fk_annotation'" :entity-value="'text'" :entity-id="selectedAnnotation.id" />
            </div>
          </div>

          <div v-if="(editMode || !selectedAnnotation.text) && selectedAnnotation.canEdit" class="mt-10px">
            <ckeditor :editor="editor" v-model="editorText" :config="config" :style="{ height: '300px' }"></ckeditor>

            <div class="mt-10px">
              <button class="btn btn-primary" @click="updateAnnotationHandler">Uložiť</button>
              <button class="btn btn-secondary ms-5px" v-show="!selectedAnnotation.text" @click="deleteAnnotationHandler">Odstrániť</button>
              <button class="btn btn-secondary ms-5px" v-show="selectedAnnotation.text" @click="this.editMode = !this.editMode;">Zrušiť</button>
            </div>
          </div>

          <p v-if="!selectedAnnotation.canEdit && !selectedAnnotation.text" class="text-gray">
            Užívateľ ju práve vyplňuje, prosím počkaj
          </p>

          <!-- COMMENTS -->
          <div class="bg-body-gray p-10px mt-15px rounded border" v-show="!editMode && selectedAnnotation.text">
            <p class="mt-0 mb-10px">
              <strong>
                <small>Komentáre k vysvetlivke</small>
              </strong>
            </p>

            <CommentEntityWrapper
                :entity-id="selectedAnnotation.id"
                :toggle-class="'annotationComments'"
                :fk_property="'annotation'"
                :comment-points="commentPoints"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createAnnotation, getAnnotation, deleteAnnotation, updateAnnotation } from "../helpers/AnnotationApi";
import { lyricsConfig, descriptionConfig } from "../ckeditor/configs";
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import CommentEntityWrapper from "../Comment/CommentEntityWrapper.vue";
import UserCreationPanel from "../User/UserCreationPanel.vue";
import VoteWrapper from "../Vote/VoteWrapper.vue";
import Report from "../Report/Report.vue";

export default {
  name: "LyricsText",
  components: { CommentEntityWrapper, UserCreationPanel, VoteWrapper, Report },
  inject: ['user', 'loginUrl'],
  emits: ['reloadLyricsText'],
  props: {
    lyrics: Object,
    text: String,
    commentPoints: Number,
  },
  data() {
    return {
      lyricsText: null,
      annotationClass: 'annotation',
      annotationElement: null,
      selectedText: '',
      selectedRange: null,
      selectedTextHtml: null,
      selectedAnnotation: null,
      offCanvas: null,
      editor: ClassicEditor,
      editorText: '',
      config: descriptionConfig,
      reloadInterval: null,
      editMode: false,
      loading: true,
    }
  },
  watch: {
    selectedText(newText) {
      newText ? this.offCanvas.show() : this.offCanvas.hide();
    },
    annotationElement(newAnnotation) {
      newAnnotation ?  this.offCanvas.show() : this.offCanvas.hide();
    },
    selectedAnnotation(newAnnotation) {
      if (newAnnotation) {
        this.offCanvas.show();
        this.editorText = newAnnotation.text ? newAnnotation.text : '<p> </p>';
      }
    },
    text(newText) {
      this.lyricsText = newText;
      clearInterval(this.reloadInterval);
      this.reloadInterval = null;
    },
  },
  computed: {
    forceReload() {
      return !this.selectedText;
    }
  },
  methods: {
    createAnnotationElement(range) {
      if (range) {
        const annotation = document.createElement('span');
        const annotationCode = (Math.random() + 1).toString(36).substring(2);

        annotation.setAttribute('id', annotationCode);
        annotation.classList.add(this.annotationClass);
        annotation.appendChild(range);

        this.selectedRange = annotation.innerHTML;

        return annotation;
      }

      return null;
    },
    resolveTextClick() {
      this.$refs.text.addEventListener('mouseup', e => {
        const target = e.target;
        const isAnnotation = target.nodeName === 'SPAN' && (target.getAttribute('class') && target.getAttribute('class').includes('annotation'));
        const selection = window.getSelection();

        if (isAnnotation) {
          this.resetActiveAnnotation();
          target.classList.add('active');
          this.selectedAnnotation = null;
          const range = selection.getRangeAt(0).cloneRange();
          this.annotationElement = this.createAnnotationElement(range.cloneContents());

          this.selectedText = target.textContent;
          this.getAnnotationHandler(target.getAttribute('id'));
        } else {
          const selectedText = selection.toString();

          if (selectedText.length > 1) {
            const range = selection.getRangeAt(0).cloneRange();
            this.selectedText = range.toString();
            const containsAnnotation = range.cloneContents().querySelectorAll('.' + this.annotationClass).length;
            this.selectedTextHtml = document.createElement('div');
            this.selectedTextHtml.appendChild(range.cloneContents());
            this.selectedTextHtml = this.selectedTextHtml.outerHTML;

            // for (let i = 0,  len = window.getSe)

            if (containsAnnotation < 1) {
              this.annotationElement = this.createAnnotationElement(range.cloneContents());
              range.extractContents();
              range.insertNode(this.annotationElement);

              this.lyricsText = this.$refs.text.innerHTML;
              this.selectedAnnotation = null;
            } else {
              this.resetAnnotation();
            }
          } else {
            this.resetAnnotation();
          }
        }
      });
    },
    resetAnnotation() {
      this.selectedText = '';
      this.selectedRange = null;
      this.annotationElement = null;
      this.lyricsText = this.text;
      this.editMode = false;
      this.selectedAnnotation = null;

      this.resetActiveAnnotation();
    },
    resetActiveAnnotation() {
      const activeAnnotation = this.$refs.text.querySelector('.annotation.active');
      if (activeAnnotation) {
        activeAnnotation.classList.remove('active');
      }
    },
    bindClickOutside() {
      document.addEventListener('click', e => {
        const lyricsElement = document.getElementById('lyrics');
        const offcanvas = document.getElementById('lyricsText');

        // if (!e.target.closest('#lyricsText') && !e.target.closest('#lyrics') && (!this.selectedAnnotation) && !this.editMode) {
        //   this.resetAnnotation();
        // }
      });
    },
    createAnnotationHandler() {
      this.editMode = true;
      const data = {
        html: this.selectedRange,
        fk_lyrics: this.lyrics.id
      }

      createAnnotation(data).then(res => {
        this.selectedAnnotation = res.data;
        this.editorText = '';
        this.editMode = false;
      });
    },
    getAnnotationHandler(id) {
      this.loading = true;
      getAnnotation(id).then(res => {
        this.selectedAnnotation = res.data;
        this.loading = false;
        this.editMode = false;
      });
    },
    updateAnnotationHandler() {
      const data = {
        id: this.selectedAnnotation.id,
        text: this.editorText
      }

      updateAnnotation(data).then(res => {
        this.selectedAnnotation = res.data;
        this.editMode = false;
      });
    },
    deleteAnnotationHandler() {
      deleteAnnotation(this.selectedAnnotation.id).then(res => {
        this.resetAnnotation();
      });
    },
    reloadLyrics() {
      this.reloadInterval = setInterval(() => {
        this.$emit('reloadLyricsText', this.forceReload);
      }, 5000);
    }
  },
  created() {
    this.lyricsText = this.text;
    this.editorText = this.selectedAnnotation && this.selectedAnnotation.text ? this.selectedAnnotation.text : '<p> </p>';
  },
  mounted() {
    this.resolveTextClick();
    this.bindClickOutside();

    this.offCanvas = new window.Offcanvas(this.$refs.offcanvas, {
      // backdrop: false
    });

    this.$refs.offcanvas.addEventListener('hidden.bs.offcanvas', () => {
      this.reset
    });

    this.reloadLyrics();
    this.loading = false;
  },
}
</script>
